import { useToast } from "@/hooks/use-toast";
import { type FeatureFlags, useFeatureAvailability, useFeatures } from "@/lib/features";
import { getTierLabel } from "@/lib/tier-labels";
import type { FeatureState } from "@/lib/use-profile-config";
import type { FeatureAvailability } from "@shared/schema/feature-availability";
import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";

interface FeatureGateProps {
  feature: keyof FeatureFlags;
  children: ReactNode;
  fallback?: ReactNode;
  profileState?: FeatureState;
  upgradeMessage?: string;
  tierRequired?: string;
}

export const FEATURE_LABELS: Record<string, string> = {
  ap: "Accounts Payable",
  ar: "Accounts Receivable",
  financing: "Financing",
  automations: "Automations",
  contractToCash: "Contract to Cash",
  aiReports: "AI Reports",
  aiChat: "AI Chat",
  multiEntity: "Multi-Entity",
  tasks: "Tasks",
  communications: "Communications",
  activityLog: "Activity Log",
  arInstruments: "AR Instruments",
  reconciliation: "Reconciliation",
  contractInvoicing: "Contract Invoicing",
  apMatching: "AP Matching",
  customPaymentGateways: "Custom Payment Gateways",
  paymentGateways: "Custom Payment Gateways",
  externalApi: "External API",
  directDebit: "Direct Debit",
  chartOfAccounts: "Chart of Accounts",
  purchaseOrders: "Purchase Orders",
  deals: "Deals",
};

function RedirectWithToast({ feature, availability }: {
  feature: string;
  availability?: FeatureAvailability;
}) {
  const { t } = useTranslation("common");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const didRedirect = useRef(false);
  const label = FEATURE_LABELS[feature] || feature;

  useEffect(() => {
    if (didRedirect.current) return;
    didRedirect.current = true;
    const reason = availability?.reason ?? "plan";
    let description: string;
    switch (reason) {
      case "geo":
        description = availability?.message
          ?? t("featureGate.notAvailableGeoDescription", { feature: label });
        break;
      case "other":
        description = availability?.message ?? t("featureGate.upgradeMessage");
        break;
      case "plan":
      case "ok":
      default: {
        const requiredTier = (availability?.details?.["requiredTier"] as string | undefined)
          ?? "growth";
        description = t("featureGate.locked", { tier: getTierLabel(requiredTier) });
        break;
      }
    }
    const title = reason === "geo"
      ? t("featureGate.notAvailableGeo", { feature: label })
      : t("featureGate.notAvailable", { feature: label });
    toast({ title, description });
    setLocation("/", { replace: true });
  }, []);

  return null;
}

export function FeatureGate({ feature, children, fallback, profileState }: FeatureGateProps) {
  const features = useFeatures();
  const { data: availability, isLoading: availabilityLoading } = useFeatureAvailability(feature);

  if (feature === "tier") return <>{children}</>;

  const enabled = features[feature];

  if (profileState === "HIDDEN" && !enabled) return null;

  // While the availability query is in-flight, return null to avoid a
  // flash of gated content. The query is stale for 60s so this is only
  // visible on first mount per session.
  if (availabilityLoading) return null;

  // OCT-11301: an *errored* availability read is UNKNOWN, not "denied".
  // `availabilityLoading || !availability` used to collapse the two, so a
  // throttled (429) `/api/features/availability` - the exact thing that
  // happens once the shared per-user bucket is drained - rendered `null`
  // forever. Every gated route (AR, AP, Automations, Tasks, Reports) then
  // showed an empty content area under a working header, with no error, no
  // retry and no recovery on `staleTime: Infinity`. That is the "invoices
  // vanish" symptom, reproduced here in the worktree against a real drained
  // bucket.
  //
  // Falling back to the tier flag degrades OPEN on *visibility* only: the
  // server re-checks the entitlement on every route the surface calls, so the
  // worst case is a user seeing a page whose reads then answer 403/402 - which
  // is a legible failure, unlike a blank rectangle. It is the same call
  // `FeaturesProvider` already makes with `data ?? DEFAULT_FLAGS`.
  //
  // DO NOT "harden" this into a fail-closed gate. This component is chrome, not
  // enforcement: entitlement is enforced server-side by
  // `useFeatureGate`/`featureGateMiddleware`
  // (`server/lib/http-platform/middleware/feature-gate.ts`), applied on every
  // gated platform sub-router - so nothing reachable through a fail-open shell
  // is actually accessible. Making it fail-closed instead blanks the whole app
  // for the duration of a throttle window, which is the OCT-11301 bug.
  // Pinned in both directions by
  // `client/src/components/__tests__/feature-gate.unit.test.tsx`.
  if (!availability) return enabled ? <>{children}</> : null;

  if (!availability.available) {
    return (
      <>
        {fallback !== undefined
          ? fallback
          : <RedirectWithToast feature={feature} availability={availability} />}
      </>
    );
  }

  return <>{children}</>;
}
